export type CountdownTimerOptions = {
  totalTimeSec: number; // 전체 시간(초)
  onTimeout?: () => void; // 0이 되었을 때 호출
};

/**
 * Phaser에 의존하지 않는 순수 타이머 로직
 * Date.now() 기반으로 경과 시간을 계산한다.
 */
export class CountdownTimer {
  private totalMs: number;
  private remainingMs: number;
  private running = false;
  private onTimeout?: () => void;

  private lastTimestamp: number | null = null;

  private speedMultiplier = 1;

  constructor(options: CountdownTimerOptions) {
    this.totalMs = options.totalTimeSec * 1000;
    this.remainingMs = this.totalMs;
    this.onTimeout = options.onTimeout;
  }

  /** 타이머 시작 */
  start() {
    this.running = true;
    this.lastTimestamp = null;
  }

  /** 일시 정지 */
  pause() {
    this.running = false;
    this.lastTimestamp = null;
  }

  /** 처음 상태로 되돌리고 다시 시작 */
  resetAndStart() {
    this.remainingMs = this.totalMs;
    this.running = true;
    this.lastTimestamp = null;
  }

  /** 타이머 완전 리셋 (하지만 자동 시작은 안함) */
  reset() {
    this.remainingMs = this.totalMs;
    this.running = false;
    this.lastTimestamp = null;
  }

  setSpeedMultiplier(multiplier: number) {
    // 안전장치: 최소 0.1배, 최대 5배 같은 식으로 제한
    this.speedMultiplier = Math.max(0.1, Math.min(5, multiplier));
  }

  /** scene.update에서 매 프레임마다 호출해주면 됨 */
  tick() {
    if (!this.running) return;

    const now = Date.now();

    if (this.lastTimestamp === null) {
      this.lastTimestamp = now;
      return;
    }

    const dt = now - this.lastTimestamp;
    this.lastTimestamp = now;

    this.addTime(-dt * this.speedMultiplier);

    if (this.remainingMs <= 0) {
      this.remainingMs = 0;
      this.running = false;
      this.lastTimestamp = null;
      if (this.onTimeout) this.onTimeout();
    }
  }

  /** 0~1 진행도 (1 = 풀게이지, 0 = 시간 끝) */
  getProgress(): number {
    if (this.totalMs <= 0) return 0;
    return this.remainingMs / this.totalMs;
  }

  /** 남은 시간(ms) */
  getRemainingMs(): number {
    return this.remainingMs;
  }

  /** 남은 시간(초) */
  getRemainingSec(): number {
    return this.remainingMs / 1000;
  }

  isRunning(): boolean {
    return this.running;
  }

  addTime(deltaMs: number) {
    this.remainingMs = Math.max(
      0,
      Math.min(this.totalMs, this.remainingMs + deltaMs),
    );
  }
}
